import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useLoading } from '../context/LoadingContext';

gsap.registerPlugin(ScrollTrigger);

const ParallaxStorySection = ({ stories, className = '' }) => {
  const sectionRef = useRef(null);
  const panelsRef = useRef([]);
  const { isInitialized } = useLoading();

  useEffect(() => {
    if (!isInitialized) return;

    const section = sectionRef.current;
    const panels = panelsRef.current;

    // Create a smoother, single timeline for each panel
    panels.forEach((panel, i) => {
      const nextPanel = panels[i + 1];

      // Grab elements within panel
      const title = panel.querySelector('h3');
      const description = panel.querySelector('p');
      const image = panel.querySelector('.image-container');

      const timeline = gsap.timeline({
        defaults: { ease: 'power2.out' },
        scrollTrigger: {
          trigger: panel,
          start: 'top 75%',   // start a bit earlier for smoother entrance
          end: 'bottom 25%',  // leave some room before fully ending
          scrub: true,
          toggleActions: 'play none none reverse',
        }
      });

      // PANEL enters
      timeline.fromTo(
        panel,
        { opacity: 0, scale: 0.9 },
        { opacity: 1, scale: 1, duration: 1 }
      );

      // TEXT elements stagger in
      timeline.from(
        [title, description].filter(Boolean),
        {
          opacity: 0,
          y: 40,
          stagger: 0.15,
          duration: 0.8,
        },
        '-=0.6' // overlap slightly with panel fade
      );

      // IMAGE parallax
      if (image) {
        gsap.to(image, {
          yPercent: 20,
          ease: 'none',
          scrollTrigger: {
            trigger: panel,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });
      }

      // PANEL exits when next panel approaches
      if (nextPanel) {
        gsap.to(panel, {
          opacity: 0,
          scale: 0.9,
          scrollTrigger: {
            trigger: nextPanel,
            start: 'top 80%',
            end: 'bottom center',
            scrub: true,
          },
        });
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, [isInitialized]);

  return (
    <section 
      ref={sectionRef}
      className={`relative ${className}`}
    >
      {stories.map((story, index) => (
        <div
          key={index}
          ref={el => panelsRef.current[index] = el}
          className="min-h-screen flex items-center justify-center p-8 sticky top-0"
        >
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h3 className="text-4xl md:text-6xl font-bold">{story.title}</h3>
              <p className="text-xl text-gray-600">{story.description}</p>
              {story.content}
            </div>
            {story.image && (
              <div className="image-container relative overflow-hidden rounded-2xl">
                <img 
                  src={story.image} 
                  alt={story.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>
        </div>
      ))}
    </section>
  );
};

export default ParallaxStorySection; 